import * as signalR from "@microsoft/signalr";

class SignalRService {
  private connections: Map<string, signalR.HubConnection> = new Map();
  private apiPathUrl: string =
    process.env.REACT_APP_API_PATH_URL || "http://localhost:5038";

  private createConnection(hub: string) {
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${this.apiPathUrl}/${hub}`, {
        accessTokenFactory: () => sessionStorage.getItem("token") || "",
      })
      .withAutomaticReconnect()
      .build();

    conn.onclose(err => console.error(`❌ ${hub} Closed:`, err));
    conn.onreconnecting(err => console.warn(`⚠️ ${hub} Reconnecting:`, err));
    conn.onreconnected(() => console.warn(`🔄 ${hub} Reconnected`));

    return conn;
  }

  // ================= START =================
  public async startConnection(hub: "notificationHub" | "chatHub" | "orderChatHub") {
    let connection = this.connections.get(hub);
    if (connection) {
      if (
        connection.state === signalR.HubConnectionState.Connected ||
        connection.state === signalR.HubConnectionState.Connecting ||
        connection.state === signalR.HubConnectionState.Reconnecting
      ) {
        return connection;
      }
    }

    if (!connection) {
      connection = this.createConnection(hub);
      this.connections.set(hub, connection);
    }

    try {
      await connection.start();
    } catch (error) {
      console.error(`❌ ${hub} Connection Error:`, error);
    }

    return connection;
  }
  // ================= STOP =================
  public async stopConnection(hub: "notificationHub" | "chatHub" | "orderChatHub") {
    const connection = this.connections.get(hub);

    if (!connection) return;

    if (connection.state === signalR.HubConnectionState.Disconnected) {
      return;
    }

    try {
      await connection.stop();
      this.connections.delete(hub);

    } catch (error) {
      console.error(`❌ Error stopping ${hub}:`, error);
    }
  }

  public async stopAllConnections() {
    const promises: Promise<void>[] = [];

    this.connections.forEach((conn, hub) => {
      if (conn.state !== signalR.HubConnectionState.Disconnected) {
        promises.push(
          conn.stop().then(() => {
            console.error(`❌ Disconnected from ${hub}`);
          })
        );
      }
    });

    await Promise.all(promises);
    this.connections.clear();
  }

  // ================= ON =================
  public on(
    hub: "notificationHub" | "chatHub" | "orderChatHub",
    event: string,
    callback: (data: any) => void
  ) {
    const connection = this.connections.get(hub);

    if (!connection) {
      console.warn(`⚠️ ${hub} not started yet`);
      return;
    }

    connection.off(event);
    connection.on(event, callback);
  }

  // ================= INVOKE =================
  public async invoke(
    hub: "notificationHub" | "chatHub" | "orderChatHub",
    method: string,
    ...args: any[]
  ) {
    const connection = this.connections.get(hub);

    if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
      console.warn(`⚠️ ${hub} not connected`);
      return;
    }

    return connection.invoke(method, ...args);
  }

  // ================= OFF =================
  public off(
    hub: "notificationHub" | "chatHub" | "orderChatHub",
    event: string,
    callback?: (...args: any[]) => void
  ) {
    const conn = this.connections.get(hub);

    if (!conn) return;

    if (callback) {
      conn.off(event, callback); // ✅ remove specific handler
    } else {
      conn.off(event); // fallback
    }
  }

  public getConnection(hub: "notificationHub" | "chatHub" | "orderChatHub") {
    return this.connections.get(hub);
  }
}
const signalRService = new SignalRService();
export default signalRService;
